import { useState, useEffect } from 'react'
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiClock } from 'react-icons/fi'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'
import { tiposPausaAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { PageHeader, Btn } from '../ui'

export default function GestionTiposPausa() {
  const { empleado } = useAuth()
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()

  const [tiposPausa, setTiposPausa] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalTipo, setModalTipo] = useState({ open: false, tipo: null })
  const [formTipo, setFormTipo] = useState({
    codigo: '',
    nombre: '',
    icono: 'FiClock',
    color: 'bg-[var(--bg-2)] text-[var(--ink-2)] border-[var(--line)]',
    orden: 0
  })

  useEffect(() => { cargarTiposPausa() }, [empresaGestionId])

  const cargarTiposPausa = async () => {
    setLoading(true)
    try {
      const response = await tiposPausaAPI.getAll()
      setTiposPausa(response.data || [])
    } catch (error) {
      console.error('Error al cargar tipos de pausa:', error)
      toast.error('Error al cargar tipos de pausa')
    } finally {
      setLoading(false)
    }
  }

  const abrirModalTipo = (tipo = null) => {
    if (tipo) {
      setFormTipo({
        codigo: tipo.codigo,
        nombre: tipo.nombre,
        icono: tipo.icono,
        color: tipo.color,
        orden: tipo.orden
      })
    } else {
      setFormTipo({
        codigo: '',
        nombre: '',
        icono: 'FiClock',
        color: 'bg-[var(--bg-2)] text-[var(--ink-2)] border-[var(--line)]',
        orden: tiposPausa.length
      })
    }
    setModalTipo({ open: true, tipo })
  }

  const cerrarModalTipo = () => {
    setModalTipo({ open: false, tipo: null })
    setFormTipo({
      codigo: '',
      nombre: '',
      icono: 'FiClock',
      color: 'bg-[var(--bg-2)] text-[var(--ink-2)] border-[var(--line)]',
      orden: 0
    })
  }

  const handleSubmitTipo = async (e) => {
    e.preventDefault()
    if (!formTipo.codigo || !formTipo.nombre) {
      toast.error('Código y nombre son obligatorios')
      return
    }
    try {
      if (modalTipo.tipo) {
        await tiposPausaAPI.update(modalTipo.tipo.id, formTipo)
        toast.success('Tipo de pausa actualizado')
      } else {
        await tiposPausaAPI.create(formTipo)
        toast.success('Tipo de pausa creado')
      }
      cargarTiposPausa()
      cerrarModalTipo()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar tipo de pausa')
    }
  }

  const eliminarTipo = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este tipo de pausa?')) return
    try {
      await tiposPausaAPI.delete(id)
      toast.success('Tipo de pausa eliminado')
      cargarTiposPausa()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar tipo de pausa')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ TP"
        kicker="Tipos de pausa"
        title="Tipos de"
        italic="pausa."
        sub="Configura los tipos de pausa disponibles para los empleados."
        actions={<Btn variant="primary" size="sm" onClick={() => abrirModalTipo()}><FiPlus size={14} /> Nuevo tipo</Btn>}
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      <div className="card p-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : tiposPausa.length === 0 ? (
          <div className="text-center py-8 text-[var(--ink-3)]">
            <FiClock className="h-12 w-12 mx-auto mb-3 text-[var(--ink-4)]" />
            <p className="font-medium">No hay tipos de pausa configurados</p>
            <p className="text-sm mt-1">Crea el primer tipo haciendo clic en "Nuevo tipo"</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tiposPausa.map((tipo) => {
              const IconComponent = FiIcons[tipo.icono] || FiClock
              return (
                <div
                  key={tipo.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-[var(--line)] hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <span className={`inline-flex items-center px-3 py-2 rounded-lg ${tipo.color}`}>
                      <IconComponent className="h-5 w-5 mr-2" />
                      <span className="font-medium">{tipo.nombre}</span>
                    </span>
                    <span className="text-xs font-mono text-[var(--ink-3)] bg-[var(--bg-2)] px-2 py-1 rounded">
                      {tipo.codigo}
                    </span>
                    <span className="text-xs text-[var(--ink-3)]">
                      Orden: {tipo.orden}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => abrirModalTipo(tipo)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors" title="Editar">
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => eliminarTipo(tipo.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors" title="Eliminar">
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal de tipo de pausa */}
      {modalTipo.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold">
                {modalTipo.tipo ? 'Editar tipo de pausa' : 'Nuevo tipo de pausa'}
              </h3>
              <button onClick={cerrarModalTipo} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]">
                <FiX className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitTipo} className="p-5 space-y-4">
              <div>
                <label className="label">Código *</label>
                <input
                  type="text"
                  value={formTipo.codigo}
                  onChange={(e) => setFormTipo({ ...formTipo, codigo: e.target.value })}
                  className="input w-full"
                  placeholder="Ej: comida, descanso"
                  required
                  disabled={!!modalTipo.tipo}
                />
                <p className="mt-1 text-xs text-[var(--ink-3)]">Identificador único (solo letras minúsculas, sin espacios)</p>
              </div>

              <div>
                <label className="label">Nombre *</label>
                <input
                  type="text"
                  value={formTipo.nombre}
                  onChange={(e) => setFormTipo({ ...formTipo, nombre: e.target.value })}
                  className="input w-full"
                  placeholder="Ej: Comida, Descanso"
                  required
                />
              </div>

              <div>
                <label className="label">Icono</label>
                <select value={formTipo.icono} onChange={(e) => setFormTipo({ ...formTipo, icono: e.target.value })} className="input w-full">
                  <option value="FiClock">Reloj</option>
                  <option value="FiCoffee">Café</option>
                  <option value="FiPause">Pausa</option>
                  <option value="FiHeart">Corazón</option>
                  <option value="FiUser">Usuario</option>
                  <option value="FiMoreHorizontal">Más</option>
                  <option value="FiSun">Sol</option>
                  <option value="FiTool">Herramienta</option>
                </select>
              </div>

              <div>
                <label className="label">Color</label>
                <select value={formTipo.color} onChange={(e) => setFormTipo({ ...formTipo, color: e.target.value })} className="input w-full">
                  <option value="bg-orange-100 text-orange-600 border-orange-200">Naranja</option>
                  <option value="bg-blue-100 text-blue-600 border-blue-200">Azul</option>
                  <option value="bg-red-100 text-red-600 border-red-200">Rojo</option>
                  <option value="bg-purple-100 text-purple-600 border-purple-200">Morado</option>
                  <option value="bg-green-100 text-green-600 border-green-200">Verde</option>
                  <option value="bg-yellow-100 text-yellow-600 border-yellow-200">Amarillo</option>
                  <option value="bg-[var(--bg-2)] text-[var(--ink-2)] border-[var(--line)]">Gris</option>
                </select>
              </div>

              <div>
                <label className="label">Orden</label>
                <input
                  type="number"
                  value={formTipo.orden}
                  onChange={(e) => setFormTipo({ ...formTipo, orden: parseInt(e.target.value) })}
                  className="input w-full"
                  min="0"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={cerrarModalTipo} className="flex-1 btn btn-secondary">Cancelar</button>
                <button type="submit" className="flex-1 btn btn-primary">
                  <FiCheck className="mr-2" />
                  {modalTipo.tipo ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
